import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import {
  useEffect, useMemo,useRef, useState
} from 'react';
import { Controller,useForm } from 'react-hook-form';
import { z } from 'zod';

import APIGateway from '@/api';
import { Button } from '@/components/button/button';
import OptionRow from '@/components/optionRow/optionRow';
import { useAgentsQuery } from '@/hooks/queries/useAgents';
import {
  EXAMPLE_TYPES, ExampleTypeEnum, SOURCE_TYPES,SourceConfigAgentLogs, SourceConfigCompletionLogs, SourceConfigModelInvocationLogs
} from '@/types/evals';

import styles from './modalSourceCreate.module.scss';
import { agentActionLogsConfigSchema, completionLogsConfigSchema, modelInvocationLogsConfigSchema } from './schemas';

type LogType = 'agent' | 'model' | 'completion';
type SourceConfigType<T extends LogType> = T extends 'agent' ? SourceConfigAgentLogs : T extends 'completion' ? SourceConfigCompletionLogs : SourceConfigModelInvocationLogs;

// `supportsMessageExampleType` reflects which MAS log adapters accept
// `example_type=MESSAGE` on the eval backend. Today only completion log was
// widened (aidp-eval PR #278); agent/model invocation log adapters still
// produce CONVERSATION-only examples. Flip the flag here if/when the server
// widens the others.
const LOG_TYPE_CONFIG = {
  agent: {
    sourceType: SOURCE_TYPES.AGENT_ACTION_LOGS,
    title: 'Agent Action Logs',
    schema: agentActionLogsConfigSchema,
    supportsMessageExampleType: false
  },
  model: {
    sourceType: SOURCE_TYPES.MODEL_INVOCATION_LOGS,
    title: 'Model Invocation Logs',
    schema: modelInvocationLogsConfigSchema,
    supportsMessageExampleType: false
  },
  completion: {
    sourceType: SOURCE_TYPES.COMPLETION_LOGS,
    title: 'Request/Response Logs',
    schema: completionLogsConfigSchema,
    supportsMessageExampleType: true
  }
};

type MasLogsConfigFormProps<LogTypeT extends LogType> = {
  logType: LogTypeT;
  initialData?: {
    example_type: ExampleTypeEnum;
    config: SourceConfigType<LogTypeT>;
  };
  source_name: string;
  source_description?: string;
  onSubmit: ( data: { example_type: ExampleTypeEnum; config: SourceConfigType<LogTypeT> } ) => void;
  onCancel: () => void;
  onBack: () => void;
  isPending?: boolean;
  isEditMode: boolean;
};

export const MasLogsConfigForm = <LogTypeT extends LogType>( {
  logType,
  initialData,
  source_name,
  source_description,
  onSubmit: onSubmitProp,
  onCancel,
  onBack,
  isPending,
  isEditMode
}: MasLogsConfigFormProps<LogTypeT> ) => {
  const [ validationSuccess, setValidationSuccess ] = useState( false );
  const [ validationError, setValidationError ] = useState<string>();
  const [ isValidating, setIsValidating ] = useState( false );

  const [ activeDropdown, setActiveDropdown ] = useState<'constellation' | 'agentName' | 'agentId' | null>( null );
  const dropdownRef = useRef<HTMLDivElement>( null );

  const { sourceType, title, schema: configSchema, supportsMessageExampleType } = LOG_TYPE_CONFIG[ logType ];

  // Fetch agents for autocomplete (enabled for all MAS log types)
  const { data: agentsData } = useAgentsQuery( { enabled: true } );

  const exampleTypeSchema = supportsMessageExampleType
    ? z.enum( [ EXAMPLE_TYPES.CONVERSATION, EXAMPLE_TYPES.MESSAGE ] )
    : z.literal( EXAMPLE_TYPES.CONVERSATION );

  const formSchema = z.object( {
    example_type: exampleTypeSchema,
    config: configSchema
  } );

  type FormData = z.infer<typeof formSchema>;

  const {
    register,
    handleSubmit,
    trigger,
    getValues,
    control,
    setValue,
    watch,
    formState: { errors }
  } = useForm<FormData>( {
    resolver: zodResolver( formSchema ),
    defaultValues: initialData ? {
      example_type: initialData.example_type,
      config: { ...initialData.config, row_limit: initialData.config.row_limit ?? undefined }
    } : {
      example_type: EXAMPLE_TYPES.CONVERSATION,
      config: {
        filters: {},
        row_limit: undefined
      }
    }
  } );

  // Watch fields for mutual exclusivity
  const watchConstellationName = watch( 'config.filters.constellation_name' );
  const watchAgentName = watch( 'config.filters.agent_name' );
  const watchAgentId = watch( 'config.filters.agent_id' );
  const exampleType = watch( 'example_type' );

  const isConstellationDisabled = !!watchAgentId || isPending;
  const isAgentNameDisabled = !!watchAgentId || !watchConstellationName || isPending;
  const isAgentIdDisabled = !!( watchConstellationName || watchAgentName ) || isPending;

  // Derive constellation options from agents data
  const constellationOptions = useMemo( () => {
    const names = ( agentsData ?? [] )
      .map( a => a.constellationName )
      .filter( ( name ): name is string => !!name );
    return [ ...new Set( names ) ].sort();
  }, [ agentsData ] );

  const filteredConstellationOptions = useMemo( () => {
    if ( !watchConstellationName ) return constellationOptions;
    return constellationOptions.filter(
      o => o.toLowerCase().includes( watchConstellationName.toLowerCase() )
    );
  }, [ constellationOptions, watchConstellationName ] );

  // Derive agent name options, narrowed by selected constellation
  const agentNameOptions = useMemo( () => {
    let agents = agentsData ?? [];
    if ( watchConstellationName ) {
      agents = agents.filter( a => a.constellationName === watchConstellationName );
    }
    const names = agents.map( a => a.name ).filter( ( name ): name is string => !!name );
    return [ ...new Set( names ) ].sort();
  }, [ agentsData, watchConstellationName ] );

  const filteredAgentNameOptions = useMemo( () => {
    if ( !watchAgentName ) return agentNameOptions;
    return agentNameOptions.filter(
      o => o.toLowerCase().includes( watchAgentName.toLowerCase() )
    );
  }, [ agentNameOptions, watchAgentName ] );

  // Derive agent ID options from agents data
  const agentIdOptions = useMemo( () => {
    const ids = ( agentsData ?? [] ).map( a => a.id ).filter( Boolean );
    return [ ...new Set( ids ) ].sort();
  }, [ agentsData ] );

  const filteredAgentIdOptions = useMemo( () => {
    if ( !watchAgentId ) return agentIdOptions;
    return agentIdOptions.filter(
      o => o.toLowerCase().includes( watchAgentId.toLowerCase() )
    );
  }, [ agentIdOptions, watchAgentId ] );

  // Close dropdown when clicking outside
  useEffect( () => {
    const handleClickOutside = ( event: MouseEvent ) => {
      if ( dropdownRef.current && !dropdownRef.current.contains( event.target as Node ) ) {
        setActiveDropdown( null );
      }
    };

    document.addEventListener( 'mousedown', handleClickOutside );
    return () => {
      document.removeEventListener( 'mousedown', handleClickOutside );
    };
  }, [] );

  // Helper function to remove empty strings and undefined values from filters
  // and rewrite bare (old-SDK) agent_id into an agent_name filter. Old agents
  // have agent_id NULL in model_invocation_log — filtering by agent_id would
  // miss their rows, so we route the bare name through agent_name instead.
  const cleanFilters = ( filters: Record<string, unknown> ) => {
    const cleaned: Record<string, unknown> = Object.fromEntries(
      Object.entries( filters ).filter( ( [ _, value ] ) => value !== '' && value !== undefined && value !== null )
    );
    const agentId = cleaned.agent_id;
    if ( typeof agentId === 'string' && !agentId.includes( '.' ) && !cleaned.agent_name ) {
      cleaned.agent_name = agentId;
      delete cleaned.agent_id;
    }
    return cleaned;
  };

  const onSubmit = ( data: FormData ) => {
    const cleanedData = {
      ...data,
      config: {
        ...data.config,
        filters: cleanFilters( data.config.filters ),
        row_limit: isNaN( data.config.row_limit as number ) ? undefined : data.config.row_limit
      }
    };
    onSubmitProp( cleanedData as { example_type: ExampleTypeEnum; config: SourceConfigType<LogTypeT> } );
  };

  const handleValidateClick = async() => {
    const isFormValid = await trigger();
    if ( !isFormValid ) return;

    setIsValidating( true );
    setValidationError( undefined );
    setValidationSuccess( false );

    try {
      const data = getValues();
      const validationData = {
        name: source_name,
        description: source_description,
        source_type: sourceType,
        config: {
          ...data.config,
          filters: cleanFilters( data.config.filters ),
          row_limit: isNaN( data.config.row_limit as number ) ? undefined : data.config.row_limit
        },
        example_type: data.example_type
      };

      const result = await APIGateway.evals.sourceValidate( validationData );

      if ( result.status === 'success' ) {
        setIsValidating( false );
        setValidationSuccess( true );
      } else {
        setIsValidating( false );
        setValidationError( result.message );
      }
    } catch( error ) {
      console.log( { error } );
      const errorMessage =
        error instanceof Error && !!error.message
          ? error.message
          : 'Validation failed. Please check your configuration and try again.';
      setIsValidating( false );
      setValidationError( errorMessage );
    }
  };

  type FilterErrors = {
    constellation_name?: { message?: string };
    agent_name?: { message?: string };
    agent_id?: { message?: string };
  };
  const filtersErrors = ( errors.config as { filters?: FilterErrors & { message?: string } } )?.filters;
  const constellationNameError = filtersErrors?.constellation_name;
  const agentNameError = filtersErrors?.agent_name;
  const agentIdError = filtersErrors?.agent_id;

  const rowLimitError = ( errors.config as { row_limit?: { message?: string } } )?.row_limit;

  return (
    <form id="source-config-form" onSubmit={ handleSubmit( onSubmit ) } className={ styles.form_wrapper }>
      <section className={ styles.layout__source_type }>
        <h2>Create Source - {title}</h2>

        {/* Constellation Name + Agent Name row */}
        <div className={ clsx( 'layout__row', 'pad' ) }>
          <div
            className={ clsx( 'layout__col', styles.date_row_col ) }
            ref={ activeDropdown === 'constellation' ? dropdownRef : undefined }
          >
            <label className="auth_flow_label" htmlFor="config.filters.constellation_name">
              Constellation Name
            </label>
            <Controller
              name="config.filters.constellation_name"
              control={ control }
              render={ ( { field } ) => (
                <div className={ styles.autocomplete_container }>
                  <input
                    className={ clsx( 'auth_flow_input', constellationNameError && styles.input_error, isConstellationDisabled && styles.input_disabled ) }
                    id="config.filters.constellation_name"
                    type="text"
                    placeholder={ isConstellationDisabled ? 'Disabled — using agent ID' : 'Select a constellation' }
                    data-testid="source-config-constellation-name"
                    disabled={ isConstellationDisabled }
                    value={ field.value || '' }
                    onChange={ ( e ) => {
                      field.onChange( e.target.value );
                      setActiveDropdown( 'constellation' );
                    } }
                    onFocus={ () => setActiveDropdown( 'constellation' ) }
                    autoComplete="off"
                  />
                  {activeDropdown === 'constellation' && filteredConstellationOptions.length > 0 && (
                    <ul className={ styles.autocomplete_dropdown } data-testid="constellation-autocomplete-dropdown">
                      {filteredConstellationOptions.map( ( option, index ) => (
                        <li
                          key={ index }
                          className={ styles.autocomplete_option }
                          onClick={ () => {
                            field.onChange( option );
                            setActiveDropdown( null );
                          } }
                          data-testid={ `constellation-option-${ index }` }
                        >
                          {option}
                        </li>
                      ) )}
                    </ul>
                  )}
                </div>
              ) }
            />
            {constellationNameError && (
              <div className={ styles.error_message }>{constellationNameError.message}</div>
            )}
          </div>

          <div
            className={ clsx( 'layout__col', styles.date_row_col ) }
            ref={ activeDropdown === 'agentName' ? dropdownRef : undefined }
          >
            <label className="auth_flow_label" htmlFor="config.filters.agent_name">
              Agent Name
            </label>
            <Controller
              name="config.filters.agent_name"
              control={ control }
              render={ ( { field } ) => (
                <div className={ styles.autocomplete_container }>
                  <input
                    className={ clsx( 'auth_flow_input', agentNameError && styles.input_error, isAgentNameDisabled && styles.input_disabled ) }
                    id="config.filters.agent_name"
                    type="text"
                    placeholder={ watchAgentId ? 'Disabled — using agent ID' : !watchConstellationName ? 'Select a constellation first' : 'Select an agent name' }
                    data-testid="source-config-agent-name"
                    disabled={ isAgentNameDisabled }
                    value={ field.value || '' }
                    onChange={ ( e ) => {
                      field.onChange( e.target.value );
                      setActiveDropdown( 'agentName' );
                    } }
                    onFocus={ () => setActiveDropdown( 'agentName' ) }
                    autoComplete="off"
                  />
                  {activeDropdown === 'agentName' && filteredAgentNameOptions.length > 0 && (
                    <ul className={ styles.autocomplete_dropdown } data-testid="agent-name-autocomplete-dropdown">
                      {filteredAgentNameOptions.map( ( option, index ) => (
                        <li
                          key={ index }
                          className={ styles.autocomplete_option }
                          onClick={ () => {
                            field.onChange( option );
                            setActiveDropdown( null );
                          } }
                          data-testid={ `agent-name-option-${ index }` }
                        >
                          {option}
                        </li>
                      ) )}
                    </ul>
                  )}
                </div>
              ) }
            />
            {agentNameError && (
              <div className={ styles.error_message }>{agentNameError.message}</div>
            )}
          </div>
        </div>

        {/* Agent ID row */}
        <div
          className={ clsx( 'layout__col', 'pad' ) }
          ref={ activeDropdown === 'agentId' ? dropdownRef : undefined }
        >
          <label className="auth_flow_label" htmlFor="config.filters.agent_id">
            Agent ID
          </label>
          <Controller
            name="config.filters.agent_id"
            control={ control }
            render={ ( { field } ) => (
              <div className={ styles.autocomplete_container }>
                <input
                  className={ clsx( 'auth_flow_input', agentIdError && styles.input_error, isAgentIdDisabled && styles.input_disabled ) }
                  id="config.filters.agent_id"
                  type="text"
                  placeholder={ isAgentIdDisabled ? 'Disabled — using constellation/agent name' : 'Select an agent ID' }
                  data-testid="source-config-agent-id"
                  disabled={ isAgentIdDisabled }
                  value={ field.value || '' }
                  onChange={ ( e ) => {
                    field.onChange( e.target.value );
                    setActiveDropdown( 'agentId' );
                  } }
                  onFocus={ () => setActiveDropdown( 'agentId' ) }
                  autoComplete="off"
                />
                {activeDropdown === 'agentId' && filteredAgentIdOptions.length > 0 && (
                  <ul className={ styles.autocomplete_dropdown } data-testid="agent-id-autocomplete-dropdown">
                    {filteredAgentIdOptions.map( ( option, index ) => (
                      <li
                        key={ index }
                        className={ styles.autocomplete_option }
                        onClick={ () => {
                          field.onChange( option );
                          setActiveDropdown( null );
                        } }
                        data-testid={ `agent-id-option-${ index }` }
                      >
                        {option}
                      </li>
                    ) )}
                  </ul>
                )}
              </div>
            ) }
          />
          {agentIdError && (
            <div className={ styles.error_message }>{agentIdError.message}</div>
          )}
        </div>

        <div className={ clsx( 'layout__row', 'pad' ) }>
          <div className={ clsx( 'layout__col', styles.date_row_col ) }>
            <label
              className="auth_flow_label"
              htmlFor="config.filters.client_id"
            >
              Client ID
            </label>
            <input
              className="auth_flow_input"
              id="config.filters.client_id"
              type="text"
              data-testid="source-config-client-id"
              disabled={ isPending }
              { ...register( 'config.filters.client_id' ) }
            />
          </div>

          <div className={ clsx( 'layout__col', styles.date_row_col ) }>
            <label
              className="auth_flow_label"
              htmlFor="config.filters.request_id"
            >
              Request ID
            </label>
            <input
              className="auth_flow_input"
              id="config.filters.request_id"
              type="text"
              data-testid="source-config-request-id"
              disabled={ isPending }
              { ...register( 'config.filters.request_id' ) }
            />
          </div>
        </div>

        <div className={ clsx( 'layout__row', 'pad' ) }>
          <div className={ clsx( 'layout__col', styles.date_row_col ) }>
            <label
              className="auth_flow_label"
              htmlFor="config.filters.from_start_time"
            >
              From start time
            </label>
            <input
              className="auth_flow_input"
              id="config.filters.from_start_time"
              type="datetime-local"
              data-testid="source-config-from-start-time"
              disabled={ isPending }
              { ...register( 'config.filters.from_start_time' ) }
            />
          </div>

          <div className={ clsx( 'layout__col', styles.date_row_col ) }>
            <label
              className="auth_flow_label"
              htmlFor="config.filters.to_end_time"
            >
              To end time
            </label>
            <input
              className="auth_flow_input"
              id="config.filters.to_end_time"
              type="datetime-local"
              data-testid="source-config-to-end-time"
              disabled={ isPending }
              { ...register( 'config.filters.to_end_time' ) }
            />
          </div>

          <div className={ clsx( 'layout__col', styles.date_row_col ) }>
            <label
              className="auth_flow_label"
              htmlFor="config.row_limit"
            >
              Row Limit
            </label>
            <input
              className={ clsx( 'auth_flow_input', rowLimitError && styles.input_error ) }
              id="config.row_limit"
              type="number"
              placeholder="Optional"
              data-testid="source-config-row-limit"
              disabled={ isPending }
              { ...register( 'config.row_limit',
                { setValueAs: ( value ) => ( value === '' || Number.isNaN( value ) ? undefined : Number( value ) ) }
              ) }
            />
            {rowLimitError && (
              <div className={ styles.error_message }>
                {rowLimitError.message}
              </div>
            )}
          </div>
        </div>

        {supportsMessageExampleType && (
          <div className={ clsx( 'layout__col', 'pad' ) } data-testid="example-type-selection">
            <OptionRow
              value={ exampleType }
              onSelect={ ( value: ExampleTypeEnum ) => setValue( 'example_type', value ) }
              title={
                (
                  <p className={ clsx( errors.example_type && 'required' ) }>
                    Select Example Type <span className={ styles.required_asterisk }>*</span>
                  </p>
                )
              }
              options={ [
                {
                  display: EXAMPLE_TYPES.CONVERSATION,
                  value: EXAMPLE_TYPES.CONVERSATION
                },
                {
                  display: EXAMPLE_TYPES.MESSAGE,
                  value: EXAMPLE_TYPES.MESSAGE
                }
              ] }
              disabled={ isEditMode || isPending }
            />
            {errors.example_type && (
              <div className={ styles.error_message }>
                {errors.example_type.message || 'Please select an example type'}
              </div>
            )}
          </div>
        )}

        {filtersErrors && !constellationNameError && !agentNameError && !agentIdError && (
          <div className={ clsx( 'layout__row', 'pad' ) }>
            <div className={ styles.error_message } data-testid="filters-error-message">
              {filtersErrors.message || 'At least one filter must be provided'}
            </div>
          </div>
        )}

        {validationError && (
          <div className={ clsx( 'layout__row', 'pad' ) }>
            <div className="layout__error" data-testid="validation-error-message">
              {validationError}
            </div>
          </div>
        )}
      </section>

      <div className={ styles.form_actions }>
        <div className={ clsx( 'layout__row', 'flex_right' ) }>
          <Button
            variant="secondary"
            disabled={ isValidating || isPending }
            onClick={ handleValidateClick }
            data-testid="source-validate-button"
          >
            {isValidating ? 'Validating' : 'Validate'}
          </Button>
          {validationSuccess && !isValidating && (
            <span className={ styles.validation_success } data-testid="validation-success-indicator">
              ✓ Valid
            </span>
          )}
        </div>
      </div>

      <div className={ styles.form_actions }>
        <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
          <Button
            variant="secondary"
            onClick={ onCancel }
            disabled={ isPending }
            data-testid="source-cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={ onBack }
            disabled={ isPending }
            data-testid="source-back-button"
          >
            Back
          </Button>
          <Button
            type="submit"
            disabled={ isPending }
            data-testid="source-create-button"
          >
            {isPending ? 'Creating...' : 'Create'}
          </Button>
        </div>
      </div>
    </form>
  );
};
